import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { InvoiceFormModal } from './InvoiceFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const buildInitialForm = (projectId, item = null) => {
  if (item) {
    return {
      project_id: item.project_id || '',
      client_id: item.client_id || '',
      payment_delay: 'custom',
      date: item.date || new Date().toISOString().slice(0, 10),
      validity_date: item.validity_date || '',
      due_date: item.due_date || '',
      notes: item.notes || '',
      client_name: item.client_name || '', client_email: item.client_email || '',
      client_phone: item.client_phone || '', client_address: item.client_address || '',
      lines: (item.lines || []).map(ln => ({
        id: ln.id, description: ln.description || '', unit: ln.unit || '',
        unit_price: ln.unit_price || 0, quantity: ln.quantity || 0,
        tva_rate: ln.tva_rate || 20, monthly_advancement: ln.monthly_advancement || 0,
        total_advancement: ln.total_advancement || 0
      }))
    };
  }
  return {
    project_id: projectId || '',
    client_id: '',
    payment_delay: 30,
    date: new Date().toISOString().slice(0, 10),
    validity_date: '',
    due_date: '',
    notes: '',
    client_name: '', client_email: '', client_phone: '', client_address: '',
    lines: [{ description: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }]
  };
};

const computeDueDate = (baseDate, delay) => {
  if (!baseDate) return '';
  const d = new Date(baseDate);
  d.setDate(d.getDate() + Number(delay || 0));
  return d.toISOString().slice(0, 10);
};

/**
 * Quick invoicing modal: open from a project detail to create a quote or invoice
 * with project_id pre-selected.
 */
export const QuickInvoiceModal = ({ show, type, projectId, item = null, onClose, onCreated }) => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [library, setLibrary] = useState([]);
  const [form, setForm] = useState(buildInitialForm(projectId, item));

  const loadData = useCallback(async () => {
    try {
      const [pRes, cRes, lRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/contacts?category=client`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/library`, { withCredentials: true }),
      ]);
      setProjects(pRes.data);
      setClients(cRes.data);
      setLibrary(lRes.data);
    } catch { toast.error('Erreur chargement'); }
  }, []);

  useEffect(() => {
    if (show) {
      loadData();
      setForm(buildInitialForm(projectId, item));
    }
  }, [show, projectId, item, loadData]);

  // Ensure project_id stays set once projects list is loaded
  useEffect(() => {
    if (show && !item && projectId && projects.length > 0) {
      setForm(f => f.project_id === projectId ? f : { ...f, project_id: projectId });
    }
  }, [show, projectId, projects, item]);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { description: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }] }));
  const removeLine = (idx) => setForm(f => f.lines.length <= 1 ? f : { ...f, lines: f.lines.filter((_, i) => i !== idx) });
  const updateLine = (idx, field, value) => setForm(f => { const nl = [...f.lines]; nl[idx] = { ...nl[idx], [field]: value }; return { ...f, lines: nl }; });
  const addFromLibrary = (li) => { setForm(f => ({ ...f, lines: [...f.lines, { description: li.description, unit: li.unit, unit_price: li.unit_price, quantity: 0, tva_rate: li.tva_rate, monthly_advancement: 0, total_advancement: 0 }] })); toast.success(`${li.description} ajoute`); };
  const calcLineHT = (ln) => (ln.unit_price || 0) * (ln.quantity || 0);
  const calcLineTTC = (ln) => calcLineHT(ln) * (1 + (ln.tva_rate || 20) / 100);
  const calcAdvHT = (ln) => calcLineHT(ln) * (ln.total_advancement || 0) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validLines = form.lines.filter(ln => ln.description.trim());
    if (validLines.length === 0) return toast.error('Ajoutez au moins un poste');
    if (type === 'devis' && !form.client_id) return toast.error('Selectionnez un client dans le repertoire');
    
    let dueDate = form.due_date;
    if (type === 'facture') {
      if (!dueDate && form.payment_delay && form.payment_delay !== 'custom') {
        dueDate = computeDueDate(form.date, form.payment_delay);
      }
      if (!dueDate) return toast.error('La date d\'echeance est obligatoire');
    }
    
    const basePayload = {
      project_id: form.project_id || null,
      client_id: form.client_id || null,
      date: form.date,
      notes: form.notes,
      client_name: form.client_name,
      client_email: form.client_email,
      client_phone: form.client_phone,
      client_address: form.client_address,
      lines: validLines,
    };
    try {
      if (type === 'devis') {
        const payload = { ...basePayload, validity_date: form.validity_date };
        if (item) {
          await axios.put(`${API_URL}/api/invoicing/quotes/${item.id}`, payload, { withCredentials: true });
          toast.success('Devis mis a jour');
        } else {
          await axios.post(`${API_URL}/api/invoicing/quotes`, payload, { withCredentials: true });
          toast.success('Devis cree');
        }
      } else {
        const payload = { ...basePayload, due_date: dueDate };
        if (item) {
          await axios.put(`${API_URL}/api/invoicing/${item.id}`, payload, { withCredentials: true });
          toast.success('Facture mise a jour');
        } else {
          await axios.post(`${API_URL}/api/invoicing`, payload, { withCredentials: true });
          toast.success('Facture creee');
        }
      }
      onClose();
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <InvoiceFormModal
      show={show} onClose={onClose} onSubmit={handleSubmit}
      form={form} setForm={setForm}
      projects={projects} clients={clients} library={library}
      modalType={type} editingItem={item}
      addLine={addLine} removeLine={removeLine} updateLine={updateLine} addFromLibrary={addFromLibrary}
      calcLineHT={calcLineHT} calcLineTTC={calcLineTTC} calcAdvHT={calcAdvHT}
    />
  );
};
